import React, { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';

// MUI
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Custom Components
import UserContext, { UserContextType } from '@/context/UserContext/UserContext';
import TenantContext, { TenantContextType } from '@/context/TenantContext/TenantContext';
import NavDrawerLink from '@/ui/NavDrawer/NavDrawerLink';
import { getNavDrawerBgLevelColors } from '@/lib/colorUtilities';

// Types
import { MenuType } from '@/@types/common';

export interface INavDrawer {
  menu: MenuType[];
  open: boolean;
  toggleOpen: () => void;
  collapsedNavLinks: string[];
  onNavLinkCollapse: (name: string) => void;
}

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});
const closedMixin = (theme: Theme, scrollbarWidth: number): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `${44 + scrollbarWidth}px`,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  minHeight: 48,
  borderTop: 0,
  borderLeft: 0,
  borderRight: 0,
  borderStyle: 'solid',
  borderColor: theme.palette.primary.main,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'scrollbarwidth',
})<{ open?: boolean; scrollbarwidth?: number }>(({ theme, open, scrollbarwidth }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme, scrollbarwidth ?? 0),
    '& .MuiDrawer-paper': closedMixin(theme, scrollbarwidth ?? 0),
  }),
}));

interface INavDrawerGroup {
  m: MenuType;
  level: number;
  open: boolean;
  isCollapsed: boolean;
  bgColor: string;
  onNavLinkCollapse: (name: string) => void;
  children: React.ReactNode;
}

// own component so each group's useState isn't called inside NavDrawer's render loop
const NavDrawerGroup = (props: INavDrawerGroup) => {
  const { m, level, open, isCollapsed, bgColor, onNavLinkCollapse, children } = props;

  const [collapsed, setCollapsed] = useState(isCollapsed);

  const handleCollapse = () => {
    setCollapsed((prevState) => !prevState);
    onNavLinkCollapse(m.name);
  };

  return (
    <>
      <Box
        key={`nav-drawer-link-container-${m.name}`}
        bgcolor={collapsed ? bgColor : 'initial'}
        sx={{
          transition: 'background-color 0.3s',
        }}
      >
        <NavDrawerLink
          level={level}
          key={`nav-drawer-link-${m.name}`}
          name={m.name}
          open={open}
          to={m.path}
          ttoken={m.ttoken}
          icon={m.icon}
          collapse={handleCollapse}
          collapsed={collapsed}
          faIconClass={m.faIconClass}
          bold
        />
        {collapsed ? <Divider key={`nav-drawer-divider-${m.name}`} component="li" /> : null}
        <Collapse aria-label={`nav-drawer-collapse-${m.name}`} key={`nav-drawer-collapse-${m.name}`} in={collapsed}>
          <List component="div" disablePadding>
            {children}
          </List>
        </Collapse>
      </Box>
      <Divider
        key={`nav-drawer-divider-${m.name}`}
        sx={{
          borderColor: isCollapsed ? 'darkgray' : undefined,
          transition: 'border-color 0.3s',
        }}
        component="li"
      />
    </>
  );
};

const NavDrawer: FunctionComponent<INavDrawer> = (props: React.PropsWithChildren<INavDrawer>) => {
  const userContext = useContext(UserContext) as UserContextType;
  const tenantContext = useContext(TenantContext) as TenantContextType;
  const { menu, open, toggleOpen, collapsedNavLinks, onNavLinkCollapse } = props;

  const [crestUrl, setCrestUrl] = useState<string>('');

  const theme = useTheme();

  const levelColors = getNavDrawerBgLevelColors(20);

  const handleDrawerClose = () => {
    toggleOpen();
  };

  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  useEffect(() => {
    const drawer = document.querySelector('#nav-drawer .MuiDrawer-paper') as HTMLElement | null;

    const checkScrollbar = () => {
      if (!drawer) return;
      const { borderLeftWidth, borderRightWidth } = getComputedStyle(drawer);
      // real space the scrollbar takes: ~15-17px classic, 0 for overlay scrollbars (Firefox on Linux, macOS)
      const width = drawer.offsetWidth - drawer.clientWidth - parseFloat(borderLeftWidth) - parseFloat(borderRightWidth);
      setScrollbarWidth(Math.max(0, Math.round(width)));
    };

    checkScrollbar();

    // Check again if the content changes or window resizes
    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (drawer) {
      resizeObserver.observe(drawer);
      // content height changes (expanding a group) can add a scrollbar without resizing the paper
      Array.from(drawer.children).forEach((child) => resizeObserver.observe(child));
    }

    window.addEventListener('resize', checkScrollbar);
    return () => {
      window.removeEventListener('resize', checkScrollbar);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const hasGrb = !!tenantContext?.tenantTheme?.crest;
    const hasSidebarGrb = !!tenantContext?.tenantTheme?.sidebar_crest;
    if (hasSidebarGrb) {
      setCrestUrl(`data:${tenantContext?.tenantTheme?.sidebar_crest_type};base64, ${tenantContext?.tenantTheme?.sidebar_crest}`);
    } else if (hasGrb) {
      setCrestUrl(`data:${tenantContext?.tenantTheme?.crest_type};base64, ${tenantContext?.tenantTheme?.crest}`);
    }
  }, [tenantContext]);

  const generateNavDrawerLink = (m: MenuType, level: number) => {
    if (m.nested && m.nested.length > 0) {
      const elements: Array<JSX.Element> = [];

      m.nested.forEach((item) => {
        if (item.needPermission && userContext.hasAnyPermission(item.needPermission)) {
          elements.push(generateNavDrawerLink(item, level + 1));
        }
      });

      return (
        <NavDrawerGroup
          key={`nav-drawer-fragment-${m.name}`}
          m={m}
          level={level}
          open={open}
          isCollapsed={collapsedNavLinks.find((x) => m.name === x) !== undefined}
          bgColor={levelColors[level]}
          onNavLinkCollapse={onNavLinkCollapse}
        >
          {elements}
        </NavDrawerGroup>
      );
    }

    return (
      <Fragment key={`nav-drawer-fragment-${m.name}`}>
        <NavDrawerLink
          level={level}
          // nested={m.nested}
          key={`nav-drawer-link-${m.name}`}
          name={m.name}
          open={open}
          to={m.path}
          ttoken={m.ttoken}
          faIconClass={m.faIconClass}
          icon={m.icon}
        />
        <Divider key={`nav-drawer-divider-${m.name}`} component="li" />
      </Fragment>
    );
  };

  return (
    <aside id="nav-drawer">
      <Drawer scrollbarwidth={scrollbarWidth} variant="permanent" open={open} sx={{}}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>{theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}</IconButton>
        </DrawerHeader>
        <Divider />
        <List disablePadding sx={{ zIndex: 20, bgcolor: theme.palette.background.paper }}>
          {menu.map((m: MenuType) => {
            if (m.needPermission !== undefined && !userContext.hasAnyPermission(m.needPermission)) {
              return null;
            }
            return generateNavDrawerLink(m, 0);
          })}
        </List>
        {crestUrl !== '' ? (
          <Box sx={{ position: 'absolute', bottom: 10, width: '100%', zIndex: 10 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={crestUrl}
                alt="logo"
                width="85%" // Don't set this at 80% or below unless you want to deal with weird bugs in Chrome
                style={{
                  transition: 'width 0.3s',
                }}
              />
            </Box>
          </Box>
        ) : null}
      </Drawer>
    </aside>
  );
};

export default NavDrawer;
